import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Grid, Button } from "semantic-ui-react";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { addDays, isSameDay, startOfWeek } from "date-fns";
import { TcCalendar } from "./TcCalendar";
import { TcCalendarFullWeek } from "./TcCalendarFullWeek";
import NotificationsList from "../notifications/NotificationsList";
import DocAllAwaitingSignatureList from "../docs/docAll/DocAllAwaitingSignatureList";
import { onSnapshot } from "firebase/firestore";
import { fetchNotificationsForUserFromDb, fetchDocsWithUnresolvedCommentsFromDb } from "../../app/firestore/firestoreService";
import { filterUpcomingDocs } from "../../app/common/util/util";
import {
  buildWeeklyDeadlineSuggestions,
  shouldShowWeeklyDeadlineSuggestions,
} from "../notifications/weeklyDeadlineEmail";

export default function TcOverview() {
  const { transActive, transClosed } = useSelector(
    (state) => state.transaction,
  );
  const { tasksAllWithDateUpcoming } = useSelector((state) => state.task);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { docsUpcoming } = useSelector((state) => state.doc);
  const [tasksForDisplay, setTasksForDisplay] = useState([{}, {}, {}, {}]);
  const [tasksForFullWeek, setTasksForFullWeek] = useState([
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
  ]);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [docsWithComments, setDocsWithComments] = useState([]);

  // Use user's calendar preference as default, fallback to "Two Weeks" if not set
  const defaultCalendarView =
    currentUserProfile?.calendarPreference === "4days" ? false : true;
  const [showFullWeek, setShowFullWeek] = useState(defaultCalendarView);

  // Update the calendar view when user profile changes
  useEffect(() => {
    if (currentUserProfile?.calendarPreference) {
      const preferredView =
        currentUserProfile.calendarPreference === "4days" ? false : true;
      setShowFullWeek(preferredView);
    }
  }, [currentUserProfile]);

  // Listen for notifications
  useEffect(() => {
    if (!currentUserProfile?.id) return;

    const unsubscribe = onSnapshot(
      fetchNotificationsForUserFromDb(currentUserProfile.id),
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNotifications(notifs);
      },
      (error) => {
        console.error("Error fetching notifications:", error);
      },
    );

    return () => unsubscribe();
  }, [currentUserProfile]);

  // Listen for documents with unresolved comments
  useEffect(() => {
    const unsubscribe = onSnapshot(
      fetchDocsWithUnresolvedCommentsFromDb(),
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          // Mark as a document comment notification type
          _isDocumentComment: true,
        }));
        setDocsWithComments(docs);
      },
      (error) => {
        console.error("Error fetching documents with unresolved comments:", error);
      },
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (tasksAllWithDateUpcoming.length > 0) {
      // Generate 4-day view (yesterday, today, tomorrow, day after tomorrow)
      let tasksByDay = [];
      [0, 1, 2, 3].forEach((day) => {
        tasksByDay[day] = tasksAllWithDateUpcoming.filter((task) =>
          isSameDay(task.end, addDays(new Date(), day - 1)),
        );
      });
      let dayDisplay = [{}, {}, {}, {}];
      tasksByDay.forEach((day, index) => {
        const dayIndex = index;
        day.forEach((task) => {
          if (dayDisplay[dayIndex]?.[task.transactionId]?.["tasks"]) {
            dayDisplay[dayIndex][task.transactionId]["tasks"].push(task);
          } else {
            let transaction = transActive.filter(
              (trans) => trans.id === task.transactionId,
            );
            if (transaction?.length > 0) {
              transaction = transaction[0];
            }
            if (!transaction) {
              transaction = transClosed.filter(
                (trans) => trans.id === task.transactionId,
              );
              if (transaction?.length > 0) {
                transaction = transaction[0];
              }
            }

            dayDisplay[dayIndex][task.transactionId] = {
              address:
                transaction?.address?.street ||
                task?.transactionTitle ||
                transaction?.title ||
                "",
              agentName: transaction?.agentProfile
                ? transaction.agentProfile.firstName +
                  " " +
                  transaction.agentProfile.lastName
                : transaction?.agentName,
              agentRepresents: transaction?.agentRepresents,
              title: transaction?.title,
              transactionId: task.transactionId,
              closingDateTime: transaction?.closingDateTime,
              tasks: [task],
            };
          }
        });
      });
      setTasksForDisplay(dayDisplay);

      // Generate full week view (Current week + Next week = 14 days)
      const startOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 0 });
      let weekTasksByDay = [];
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].forEach((day) => {
        weekTasksByDay[day] = tasksAllWithDateUpcoming.filter((task) =>
          isSameDay(task.end, addDays(startOfCurrentWeek, day)),
        );
      });
      let weekDayDisplay = [
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
      ];
      weekTasksByDay.forEach((day, index) => {
        const dayIndex = index;
        day.forEach((task) => {
          if (weekDayDisplay[dayIndex]?.[task.transactionId]?.["tasks"]) {
            weekDayDisplay[dayIndex][task.transactionId]["tasks"].push(task);
          } else {
            let transaction = transActive.filter(
              (trans) => trans.id === task.transactionId,
            );
            if (transaction?.length > 0) {
              transaction = transaction[0];
            }
            if (!transaction) {
              transaction = transClosed.filter(
                (trans) => trans.id === task.transactionId,
              );
              if (transaction?.length > 0) {
                transaction = transaction[0];
              }
            }

            weekDayDisplay[dayIndex][task.transactionId] = {
              address:
                transaction?.address?.street ||
                task?.transactionTitle ||
                transaction?.title ||
                "",
              agentName: transaction?.agentProfile
                ? transaction.agentProfile.firstName +
                  " " +
                  transaction.agentProfile.lastName
                : transaction?.agentName,
              agentRepresents: transaction?.agentRepresents,
              title: transaction?.title,
              transactionId: task.transactionId,
              closingDateTime: transaction?.closingDateTime,
              tasks: [task],
            };
          }
        });
      });
      setTasksForFullWeek(weekDayDisplay);
    }
    setLoading(true);
  }, [tasksAllWithDateUpcoming, transActive, transClosed]);

  if (!loading) return <LoadingComponent content="Loading details..." />;

  const docsUpcomingFiltered = docsUpcoming
    ? filterUpcomingDocs(docsUpcoming)
    : [];

  const weeklyDeadlineSuggestions = shouldShowWeeklyDeadlineSuggestions(
    currentUserProfile,
  )
    ? buildWeeklyDeadlineSuggestions({
        transActive: transActive || [],
        tasksAllWithDateUpcoming: tasksAllWithDateUpcoming || [],
      })
    : [];

  return (
    <div className="main-page-wrapper">
      <Grid className="medium bottom margin">
        <Grid.Column computer={16}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "36px",
            }}
          >
            <h3 style={{ margin: 0 }}>Calendar</h3>
            <Button.Group size="small">
              <Button
                active={!showFullWeek}
                onClick={() => setShowFullWeek(false)}
              >
                4 Days
              </Button>
              <Button
                active={showFullWeek}
                onClick={() => setShowFullWeek(true)}
              >
                Two Weeks
              </Button>
            </Button.Group>
          </div>
          <div className="small horizontal padding">
            {showFullWeek ? (
              <TcCalendarFullWeek tasksByDay={tasksForFullWeek} />
            ) : (
              <TcCalendar tasksByDay={tasksForDisplay} />
            )}
          </div>
        </Grid.Column>
      </Grid>
      <Grid stackable>
        <Grid.Column computer={10}>
          <h3 className="mini bottom margin">Notifications and Suggestions</h3>
          {notifications.length > 0 ||
          docsWithComments.length > 0 ||
          weeklyDeadlineSuggestions.length > 0 ? (
            <NotificationsList
              notifications={notifications}
              docsWithComments={docsWithComments}
              weeklyDeadlineSuggestions={weeklyDeadlineSuggestions}
            />
          ) : (
            <p>There are no notifications</p>
          )}
        </Grid.Column>
        <Grid.Column computer={6}>
          <h3 className="mini bottom margin">Documents Awaiting Signature</h3>
          {docsUpcomingFiltered.length > 0 ? (
            <DocAllAwaitingSignatureList docs={docsUpcomingFiltered} />
          ) : (
            <p>There are no documents awaiting signature</p>
          )}
        </Grid.Column>
      </Grid>
    </div>
  );
}
